import React, { useState } from 'react';
import { Briefcase, Pencil, Trash2, Plus } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';

/**
 * Employment history — the roles somebody held BEFORE this job.
 *
 * Shared by the manager's Add & Edit Staff form and the candidate's own
 * information form, because it is the same list either way: a candidate
 * declares their history, and a manager adds or corrects it. One component
 * means the two can never ask for different things.
 *
 * Deliberately separate from the employment section above it, which describes
 * the post being FILLED. Each entry carries a reason for leaving and a gap
 * explanation because care work is regulated — a recruiter has to be able to
 * account for a history and explain the gaps in it, which a bare list of
 * employer names cannot do.
 *
 * Entries are added through a dialog rather than inline: there are seven fields
 * per role, and a growing stack of seven-field blocks buries the rest of the
 * step. The list stays a short, readable summary; the detail is one click away.
 *
 * @param {Array}    value     entries: { id, employer, job_title, start_date,
 *                             end_date, still_employed, reason_for_leaving,
 *                             gap_explanation }
 * @param {Function} onChange  called with the next array
 * @param {boolean}  readOnly  list only, no add/edit/remove
 */

const BLANK = {
  id: '',
  employer: '',
  job_title: '',
  start_date: '',
  end_date: '',
  still_employed: false,
  reason_for_leaving: '',
  gap_explanation: '',
};

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

/** "Mar 2019 – Jan 2022", "Mar 2019 – present", or null when neither is set. */
const periodOf = (entry) => {
  const from = fmtDate(entry.start_date);
  const to = entry.still_employed ? 'present' : fmtDate(entry.end_date);
  if (!from && !to) return null;
  if (!from) return `until ${to}`;
  if (!to) return `from ${from}`;
  return `${from} – ${to}`;
};

const INPUT = 'w-full px-4 py-2.5 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#E2E8F0] dark:border-gray-700 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all';
const LABEL = 'block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5';

const EmploymentHistory = ({ value, onChange, readOnly = false }) => {
  const entries = Array.isArray(value) ? value : [];

  // The entry being added or edited, or null when the dialog is closed.
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  const openAdd = () => {
    setError(null);
    // A client-side id, so an entry can be edited and removed before the record
    // it belongs to has ever been saved.
    setDraft({ ...BLANK, id: `hist-${Date.now()}` });
  };

  const openEdit = (entry) => {
    setError(null);
    setDraft({ ...BLANK, ...entry });
  };

  const closeDraft = () => {
    setDraft(null);
    setError(null);
  };

  const setField = (field, next) => setDraft((prev) => ({ ...prev, [field]: next }));

  const save = () => {
    if (!draft.employer.trim()) {
      setError('Please enter the employer or organisation.');
      return;
    }
    // Ticking "still employed here" and leaving an end date behind would
    // contradict itself the moment anybody read it back.
    const entry = {
      ...draft,
      employer: draft.employer.trim(),
      end_date: draft.still_employed ? '' : draft.end_date,
    };

    const exists = entries.some((e) => e.id === entry.id);
    onChange(exists
      ? entries.map((e) => (e.id === entry.id ? entry : e))
      : [...entries, entry]);
    closeDraft();
  };

  const remove = (id) => {
    if (!window.confirm('Remove this previous employment?')) return;
    onChange(entries.filter((e) => e.id !== id));
  };

  return (
    <div>
      {entries.length > 0 && (
        <div className="space-y-2.5 mb-4">
          {entries.map((entry) => {
            const period = periodOf(entry);
            return (
              <div
                key={entry.id}
                className="flex items-start justify-between gap-4 px-4 py-3 rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c]"
              >
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">
                    {entry.employer}
                    {entry.still_employed && (
                      <span className="ml-2 align-middle px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold">
                        Current
                      </span>
                    )}
                  </p>
                  <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 truncate">
                    {[entry.job_title, period].filter(Boolean).join(' · ') || 'No dates given'}
                  </p>
                  {/* The two things a recruiter actually has to be able to
                      explain, so they are visible without opening the entry. */}
                  {entry.reason_for_leaving && (
                    <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1">
                      <span className="font-semibold">Left because: </span>{entry.reason_for_leaving}
                    </p>
                  )}
                  {entry.gap_explanation && (
                    <p className="text-[12px] text-amber-700 dark:text-amber-500 mt-1">
                      <span className="font-semibold">Gap: </span>{entry.gap_explanation}
                    </p>
                  )}
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEdit(entry)}
                      aria-label={`Edit ${entry.employer}`}
                      className="p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:text-[#2563EB] hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(entry.id)}
                      aria-label={`Remove ${entry.employer}`}
                      className="p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#CBD5E1] dark:border-gray-700 text-[13.5px] font-bold text-[#475569] dark:text-gray-300 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors"
        >
          <Plus className="w-4 h-4" /> Add previous employment
        </button>
      )}

      {readOnly && entries.length === 0 && (
        <p className="text-[13px] text-[#94A3B8] dark:text-gray-500">No previous employment recorded.</p>
      )}

      <Modal
        isOpen={!!draft}
        onClose={closeDraft}
        title={(
          <span className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#2563EB]" />
            {entries.some((e) => e.id === draft?.id) ? 'Edit previous employment' : 'Add previous employment'}
          </span>
        )}
        maxWidth="max-w-2xl"
      >
        {draft && (
          <div className="space-y-5">
            <p className="text-[13px] text-[#64748B] dark:text-gray-400">
              Add the details you have. You can come back and edit them.
            </p>

            {error && (
              <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 px-4 py-2.5 rounded-xl text-[13px] font-medium">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <div>
                <label className={LABEL}>Employer / organisation <span className="text-rose-500">*</span></label>
                <input
                  className={INPUT}
                  value={draft.employer}
                  onChange={(e) => setField('employer', e.target.value)}
                  placeholder="e.g. Brookside Lodge"
                  autoFocus
                />
              </div>
              <div>
                <label className={LABEL}>Job title / role</label>
                <input
                  className={INPUT}
                  value={draft.job_title}
                  onChange={(e) => setField('job_title', e.target.value)}
                  placeholder="e.g. Senior Care Assistant"
                />
              </div>
              <div>
                <label className={LABEL}>Start date</label>
                <input
                  type="date"
                  className={INPUT}
                  value={draft.start_date || ''}
                  onChange={(e) => setField('start_date', e.target.value)}
                />
              </div>
              <div>
                <label className={LABEL}>End date</label>
                <input
                  type="date"
                  className={INPUT}
                  value={draft.end_date || ''}
                  disabled={draft.still_employed}
                  onChange={(e) => setField('end_date', e.target.value)}
                />
                <label className="inline-flex items-center gap-2 mt-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-blue-600"
                    checked={draft.still_employed}
                    onChange={(e) => setField('still_employed', e.target.checked)}
                  />
                  <span className="text-[13px] font-medium text-[#475569] dark:text-gray-300">
                    Still employed here
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className={LABEL}>Reason for leaving <span className="font-normal text-[#94A3B8]">(if applicable)</span></label>
              <textarea
                rows={3}
                className={INPUT}
                value={draft.reason_for_leaving}
                onChange={(e) => setField('reason_for_leaving', e.target.value)}
                placeholder="e.g. Moved to be closer to family"
              />
            </div>

            <div>
              <label className={LABEL}>Employment gaps / explanation <span className="font-normal text-[#94A3B8]">(if applicable)</span></label>
              <textarea
                rows={3}
                className={INPUT}
                value={draft.gap_explanation}
                onChange={(e) => setField('gap_explanation', e.target.value)}
                placeholder="e.g. Six months out to study, Jan–Jun 2021"
              />
              <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1.5">
                Care roles are regulated, so any gap between jobs needs a short explanation.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={closeDraft}>Cancel</Button>
              <Button type="button" variant="primary" onClick={save}>Save employment</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmploymentHistory;
